import { useCallback, useState, useEffect } from "react";
import { Plus, Minus, ShoppingCart, Trash2, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Services from "../lib/services";
import { useNotifications } from "../contexts/NotificationContext";

const ACCENT = "linear-gradient(135deg, #6f12ff 0%, #9c27ff 100%)";

const shellStyle = {
  minHeight: "100vh",
  background: "#f3f0f8",
  padding: "20px 24px 40px",
};

const containerStyle = {
  maxWidth: "1200px",
  margin: "0 auto",
};

const headerStyle = {
  display: "grid",
  gridTemplateColumns: "34px 1fr 34px",
  alignItems: "center",
  marginBottom: 28,
};

const formatMoney = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const SummaryRow = ({ label, amount, highlight = false }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
    <span style={{ fontSize: 11, color: highlight ? "#16141d" : "#817a8e", fontWeight: highlight ? 700 : 500 }}>{label}</span>
    <span style={{ fontSize: 11, color: "#16141d", fontWeight: highlight ? 800 : 600 }}>{formatMoney(amount)}</span>
  </div>
);

export default function LabCart() {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const [cart, setCart] = useState([]);
  const [bookingInfo, setBookingInfo] = useState({ address: "", date: "", time: "" });
  const [loading, setLoading] = useState(false);
  const [showSuccessSheet, setShowSuccessSheet] = useState(false);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  // Load cart from localStorage
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("labCart")) || [];
    setCart(saved);
  }, []);

  const removeFromCart = (id) => {
    const updated = cart.filter((c) => c.id !== id);
    setCart(updated);
    localStorage.setItem("labCart", JSON.stringify(updated));
  };

  const changeQty = (id, qty) => {
    if (qty < 1) return;
    const updated = cart.map((c) => (c.id === id ? { ...c, qty: Math.max(1, qty) } : c));
    setCart(updated);
    localStorage.setItem("labCart", JSON.stringify(updated));
  };

  const subtotal = cart.reduce((sum, it) => sum + (it.price || 0) * (it.qty || 1), 0);
  const total = Number(subtotal.toFixed(2));

  const handleBook = async () => {
    if (!cart.length) return alert("Select at least one test.");
    if (!bookingInfo.address || !bookingInfo.date || !bookingInfo.time)
      return alert("Fill in address, date, and time for sample pickup.");

    setLoading(true);
    try {
      const payload = {
        user: localStorage.getItem('userId'),
        items: cart.map((c) => ({ testId: c.id, qty: c.qty })),
        ...bookingInfo,
      };


      const data = {
        userId: localStorage.getItem("userId"),
        providerId: "6996f83cad941d32a067a6f4",
        amount: total,
        type: "lab-test",
        serviceDetails: "Lab Test booked ",
      };
      const payment = await Services.wallet.payProvider(data);
      if (!payment?.success) {
        alert(payment?.message || "Payment failed");
        return;
      }

      const res = await api.post("/api/lab-bookings", payload).catch(() => null);
      const testNames = cart.map((c) => c.name || c.id).join(", ");
      addNotification({
        type: "lab_booked",
        title: "Lab Test Booked",
        body: `Your lab test${cart.length > 1 ? "s" : ""} (${testNames}) have been booked for ${bookingInfo.date} at ${bookingInfo.time}`,
        icon: "🧪",
      });
      setCart([]);
      localStorage.removeItem("labCart");
      setBookingInfo({ address: "", date: "", time: "" });

      // Log activity
      try {
        await api.post('/api/activities/add-activity', { message: 'Booked lab tests' });
      } catch (err) {
        console.error('Failed to log lab booking activity', err);
      }
      setShowSuccessSheet(true);
    } catch (err) {
      console.error(err);
      alert("Failed to book tests.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={shellStyle}>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <i
            className="fi fi-br-angle-left"
            onClick={() => navigate(-1)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              fontSize: "20px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              color: "#2d2536",
              padding: "6px 10px",
              cursor: "pointer",
              display: "inline-flex",
            }}
          ></i>

          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#18141f", textAlign: "center" }}>Cart</h1>

          <div style={{ position: "relative", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShoppingCart size={18} color="#2d2536" />
            <span
              style={{
                position: "absolute",
                top: 3,
                right: 1,
                minWidth: 14,
                height: 14,
                borderRadius: 999,
                background: "#ff4b6e",
                color: "#fff",
                fontSize: 9,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 3px",
              }}
            >
              {cart.length}
            </span>
          </div>
        </div>

        {cart.length === 0 ? (
          <div style={{ padding: "40px 14px 24px", textAlign: "center" }}>
            <div
              style={{
                width: 86,
                height: 86,
                borderRadius: 28,
                margin: "0 auto 16px",
                background: "linear-gradient(180deg, #f3e8ff 0%, #ede9fe 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCart size={32} color="#7b1cff" />
            </div>
            <h2 style={{ margin: "0 0 8px", fontSize: 20, color: "#18141f" }}>Your cart is empty</h2>
            <p style={{ margin: "0 0 22px", color: "#7f788d", fontSize: 14 }}>Add tests to start building your lab order.</p>
            <button
              type="button"
              onClick={() => navigate("/lab-tests")}
              style={{
                width: "100%",
                border: "none",
                borderRadius: 16,
                padding: "15px 18px",
                color: "#fff",
                fontWeight: 700,
                background: ACCENT,
                boxShadow: "0 18px 28px rgba(124, 58, 237, 0.28)",
                cursor: "pointer",
              }}
            >
              Browse Lab Tests
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gap: 14 }}>
              {cart.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "60px 1fr auto",
                    gap: 14,
                    alignItems: "center",
                    padding: "12px 14px",
                    borderRadius: 18,
                    background: "#ffffff",
                    border: "1px solid #f0ecf7",
                    boxShadow: "0 6px 18px rgba(77, 30, 126, 0.07)",
                  }}
                >
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 16,
                      overflow: "hidden",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #f3eff8",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={item.image || "https://via.placeholder.com/80x80?text=Lab"}
                      alt={item.title || "Lab test"}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#18141f", marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.title || "Lab test"}
                    </div>
                    <div style={{ fontSize: 10, color: "#938aa4", marginBottom: 6 }}>
                      {item.category || "Diagnostic"}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#18141f" }}>{formatMoney((item.price || 0) * (item.qty || 1))}</div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <button
                        type="button"
                        onClick={() => changeQty(item.id, item.qty - 1)}
                        disabled={loading || (item.qty || 1) <= 1}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 7,
                          border: "1px solid #ece7f4",
                          background: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#9b93ab",
                          cursor: "pointer",
                        }}
                      >
                        <Minus size={12} />
                      </button>
                      <span style={{ minWidth: 10, textAlign: "center", fontSize: 12, fontWeight: 700, color: "#2d2536" }}>{item.qty || 1}</span>
                      <button
                        type="button"
                        onClick={() => changeQty(item.id, (item.qty || 1) + 1)}
                        disabled={loading}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 7,
                          border: "none",
                          background: ACCENT,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          cursor: "pointer",
                          boxShadow: "0 8px 14px rgba(124, 58, 237, 0.2)",
                        }}
                      >
                        <Plus size={12} />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeFromCart(item.id)}
                      disabled={loading}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#c25a6a",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 10,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => navigate("/lab-tests")}
              style={{
                marginTop: 6,
                width: "100%",
                padding: "11px 12px",
                borderRadius: 12,
                border: "1px solid #eee8f7",
                background: "#fff",
                color: "#2d2536",
                fontSize: 12,
                fontWeight: 600,
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              + Add Tests
            </button>

            <div
              style={{
                marginTop: 20,
                padding: "18px 18px 14px",
                borderRadius: 18,
                background: "#ffffff",
                border: "1px solid #f0ecf7",
                boxShadow: "0 6px 18px rgba(77, 30, 126, 0.07)",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: "#18141f", marginBottom: 12 }}>Booking details</div>

              
              <input
                type="text"
                placeholder="Your address for sample pickup"
                value={bookingInfo.address}
                onChange={(e) => setBookingInfo({ ...bookingInfo, address: e.target.value })}
                style={{
                  width: "100%",
                  border: "1px solid #e8e3f0",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 12,
                  marginBottom: 10,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <input
                  type="date"
                  value={bookingInfo.date}
                  onChange={(e) => setBookingInfo({ ...bookingInfo, date: e.target.value })}
                  style={{
                    width: "100%",
                    border: "1px solid #e8e3f0",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 12,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <input
                  type="time"
                  value={bookingInfo.time}
                  onChange={(e) => setBookingInfo({ ...bookingInfo, time: e.target.value })}
                  style={{
                    width: "100%",
                    border: "1px solid #e8e3f0",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 12,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: "#18141f", marginBottom: 12 }}>Cost summary</div>
              <SummaryRow label="Subtotal" amount={subtotal} />
              <SummaryRow label="Taxes" amount={0} />
              <SummaryRow label="Other Fees" amount={0} />
              <SummaryRow label="Delivery Fees" amount={0} />
              <div style={{ height: 1, background: "#ece7f4", margin: "12px 0" }} />
              <SummaryRow label="Total cost" amount={total} highlight />

              <button
                type="button"
                onClick={handleBook}
                disabled={loading}
                style={{
                  marginTop: 14,
                  width: "100%",
                  border: "none",
                  borderRadius: 14,
                  padding: "15px 18px",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  background: ACCENT,
                  boxShadow: "0 18px 24px rgba(124, 58, 237, 0.28)",
                  cursor: "pointer",
                }}
              >
                {loading ? "Booking..." : "Book Tests"}
              </button>
            </div>
          </>
        )}
      </div>

      {showSuccessSheet && (
        <>
          <div
            onClick={() => setShowSuccessSheet(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 12, 24, 0.45)",
              zIndex: 1200,
            }}
          />
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              background: "#ffffff",
              borderTopLeftRadius: 28,
              borderTopRightRadius: 28,
              padding: "14px 16px 24px",
              zIndex: 1201,
              textAlign: "center",
            }}
          >
            <div style={{ width: 46, height: 5, borderRadius: 999, background: "#ece8f5", margin: "0 auto 18px" }} />
            <div style={{ width: 40, height: 40, borderRadius: 999, margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center", background: "#7f18f4" }}>
              <CheckCircle size={18} color="#fff" />
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: 34, fontWeight: 700, color: "#15121f" }}>Congratulations!!!</h3>
            <p style={{ margin: "0 0 18px", color: "#6f6a7d", fontSize: 16, lineHeight: 1.45 }}>
              Congratulations, your lab test booking has been placed successfully.
            </p>
            <button
              type="button"
              onClick={() => navigate("/active-tests")}
              className="btn btn-accent w-100"
              style={{ borderRadius: 14, fontWeight: 700, fontSize: 16, padding: "12px 16px" }}
            >
              Track my Booking
            </button>
          </div>
        </>
      )}
    </div>
  );
}
